import * as Location from 'expo-location';

import Sentry from '@lib/analytics/sentry';
import { isApiError } from '@lib/api';
import StoreService from '@lib/api/StoreService';
import { CONTACT_US_URL } from '@lib/constants/Strings';
import { type DeliveryCheckPayload } from '@lib/entities/Address';
import { type StreetAddress } from '@lib/entities/Address';
import { type DeliveryType } from '@lib/entities/Store';
import useSchedule from '@lib/hooks/useSchedule';
import useStoreDirectory from '@lib/hooks/useStoreDirectory';
import useToast from '@lib/hooks/useToast';
import { type ApplicationState, useApplicationState } from '@lib/state';
import { distance as calculateDistance } from '@lib/utils/distance';

const selectDelivery = (s: ApplicationState) => s.delivery;

const DistanceThreshold = 1; // miles

interface DeliveryCheckParams {
    address: StreetAddress;
    onSuccess: (storeId: number) => void;
    onFailure: (params?: { willDeliver?: boolean; isOpen?: boolean }) => void;
}

export const useDelivery = () => {
    const {
        checkingDeliveryAddress,
        checkedDeliveryAddress,
        changeDeliveryType,
        ...deliveryState
    } = useApplicationState(selectDelivery);
    const { reset: resetSchedule, fetchTimeSlots, store } = useSchedule();
    const { hasForegroundLocationPermissions } = useStoreDirectory();
    const { toast } = useToast();

    const handleChangeDeliveryType = (deliveryType: DeliveryType) => {
        changeDeliveryType(deliveryType);

        // Pass the store through the reset (same as useVisitStore) — the user is
        // switching HOW they get their order, not WHERE from. The argument-less
        // reset blanked `schedule.store` until fetchTimeSlots resolved, and
        // everything gated on a selected store degraded in that window: add-to-bag's
        // `if (store)` opened the LOCATION modal instead of the cart drawer
        // (MenuItemEditor Editor.tsx), which is the delivery half of the nightly
        // cart-drawer failure class. reset() bumps fetchSeq, so any in-flight slot
        // fetch from before the switch is already invalidated.
        resetSchedule(store);
        if (!store) {
            return;
        }

        fetchTimeSlots({ store, deliveryType });
    };

    const checkDelivery = async ({ address, onSuccess, onFailure }: DeliveryCheckParams) => {
        checkingDeliveryAddress();
        let check: DeliveryCheckPayload | undefined = undefined;
        try {
            const response = await StoreService.checkDeliveryAddress(address);

            if (isApiError(response)) {
                onFailure();
                return;
            }

            check = response;

            if (check.willDeliver) {
                if (!check.storeId) {
                    console.error(
                        `[useDelivery/checkDelivery] missing store id for successful delivery check. ${check}`
                    );
                    onFailure({ willDeliver: check.willDeliver, isOpen: check.isOpen });
                    return;
                }
                onSuccess(check.storeId);
                return;
            }
            onFailure({ willDeliver: check.willDeliver, isOpen: check.isOpen });
        } catch (error) {
            console.error(error);
            Sentry.captureException(error);
        } finally {
            checkedDeliveryAddress(check);
        }
    };

    /**
     * Returns true of the user is more than a mile away from their delivery
     * address.
     */
    const isFarAway = async () => {
        if (!hasForegroundLocationPermissions) {
            return false;
        }

        if (deliveryState.isPickup) {
            return false;
        }

        const deliveryAddress = deliveryState.deliveryAddress;

        if (!deliveryAddress) {
            console.warn('[useDelivery/isFarAway] isPickup false but missing delivery address');
            return false;
        }

        try {
            const location = await Location.getCurrentPositionAsync();
            const distance = calculateDistance(
                parseFloat(deliveryAddress.lat),
                parseFloat(deliveryAddress.lng),
                location.coords.latitude,
                location.coords.longitude,
                'M'
            );

            return distance > DistanceThreshold;
        } catch (error) {
            // No device location available (denied / no provider / headless
            // browser). Best-effort distance check — can't compute, so treat
            // as not-far and never let it reject unhandled.
            console.warn('[useDelivery/isFarAway] current position unavailable', error);
            return false;
        }
    };

    const showErrorMessage = (params?: { willDeliver?: boolean; isOpen?: boolean }) => {
        if (!params?.isOpen) {
            toast({
                message: 'Stores delivering to this address are currently closed',
                level: 'error'
            });
            return;
        }
        if (!params?.willDeliver) {
            toast({
                message: "We don't deliver to this address",
                level: 'error'
            });
            return;
        }
        toast({
            message: `Something went wrong when checking this address, please try again or contact us at ${CONTACT_US_URL}`,
            level: 'error'
        });
    };

    return {
        ...deliveryState,
        changeDeliveryType: handleChangeDeliveryType,
        checkDelivery,
        isFarAway,
        showErrorMessage
    };
};

export default useDelivery;
